import { useMemo, useState, useCallback } from 'react';

import { useGetLocationAddressesQuery, LocationAddressFilterType } from '@/entities/location';

import type { SelectOption } from '@/shared/ui';
import { useDebounce, useProjectSiteParams } from '@/shared/lib';
import type { AllowedServiceTypes } from './options';

interface UseServiceIdentificationProps {
  serviceType?: AllowedServiceTypes;
}

export function useServiceIdentification({ serviceType }: UseServiceIdentificationProps) {
  const { projectId, siteId } = useProjectSiteParams();
  const [searchValue, setSearchValue] = useState('');

  const filterType = serviceType ? LocationAddressFilterType[serviceType] : null;
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const { data: locationAddresses, isLoading } = useGetLocationAddressesQuery(
    {
      projectId: Number(projectId),
      siteId: Number(siteId),
      type: filterType!,
      search: debouncedSearchValue
    },
    {
      skip: !filterType || !projectId || !siteId
    }
  );

  const locationOptions: SelectOption[] = useMemo(() => {
    if (!locationAddresses) return [];

    return locationAddresses.map((location) => {
      const parts = [location.name];
      if (location.city) parts.push(location.city);
      if (location.country) parts.push(location.country);

      return {
        value: location.id,
        label: parts.join(', ')
      };
    });
  }, [locationAddresses]);

  const resetSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  return {
    locationOptions,
    isLoading,
    searchValue,
    setSearchValue,
    resetSearch
  };
}
